import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import ServiceAreaSettings from '../../components/ServiceAreaSettings';
import BlackoutDatesSettings from '../../components/BlackoutDatesSettings';
import DynamicPricingSettings from '../../components/DynamicPricingSettings';
import SMSNotificationSettings from '../../components/SMSNotificationSettings';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/**
 * Admin Settings Tab
 *
 * Presentational component. All state lives in the parent (AdminDashboardFull).
 * This component only renders JSX and calls handlers passed via props.
 */
const AdminSettingsTab = ({
  settings,
  setSettings,
  updateSettings,
  settingsMessage,
  usernameForm,
  setUsernameForm,
  changeUsername,
  passwordForm,
  setPasswordForm,
  changePassword,
}) => {
  return (
    <div data-testid="admin-settings-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700' }}>Admin Settings</h2>
      </div>

      {settingsMessage && (
        <div style={{
          padding: '1rem',
          backgroundColor: settingsMessage.includes('success') ? '#f0fdf4' : '#fef2f2',
          color: settingsMessage.includes('success') ? '#15803d' : '#dc2626',
          borderRadius: '8px',
          marginBottom: '1rem'
        }} data-testid="settings-message">
          {settingsMessage}
        </div>
      )}

      <div style={{ display: 'grid', gap: '1.5rem', maxWidth: '800px' }}>

        {/* Change Username Card */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontSize: '1.25rem' }}>Change Admin Username</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                  New Username
                </label>
                <input
                  type="text"
                  value={usernameForm.newUsername}
                  onChange={(e) => setUsernameForm({ ...usernameForm, newUsername: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }}
                  placeholder="Enter new username (min 3 characters)"
                  data-testid="new-username-input"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={usernameForm.confirmPassword}
                  onChange={(e) => setUsernameForm({ ...usernameForm, confirmPassword: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }}
                  placeholder="Enter your current password to confirm"
                  data-testid="username-confirm-password-input"
                />
              </div>

              <Button
                onClick={changeUsername}
                style={{ backgroundColor: '#3b82f6', color: 'white', padding: '0.75rem 1.5rem', width: 'fit-content' }}
                data-testid="update-username-btn"
              >
                Update Username
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Change Password Card */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontSize: '1.25rem' }}>Change Admin Password</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }}
                  placeholder="Enter current password"
                  data-testid="current-password-input"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }}
                  placeholder="Enter new password (min 8 characters)"
                  data-testid="new-password-input"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }}
                  placeholder="Confirm new password"
                  data-testid="confirm-new-password-input"
                />
              </div>

              <Button
                onClick={changePassword}
                style={{ backgroundColor: '#10b981', color: 'white', padding: '0.75rem 1.5rem', width: 'fit-content' }}
                data-testid="update-password-btn"
              >
                Update Password
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Business Information Card */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontSize: '1.25rem' }}>Business Information</CardTitle>
          </CardHeader>
          <CardContent>
            {!settings ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <p style={{ color: '#6b7280' }}>Loading settings...</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                    Business Name
                  </label>
                  <input
                    type="text"
                    value={settings.business_name || ''}
                    onChange={(e) => setSettings({ ...settings, business_name: e.target.value })}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }}
                    data-testid="business-name-input"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                    Business Email
                  </label>
                  <input
                    type="email"
                    value={settings.business_email || ''}
                    onChange={(e) => setSettings({ ...settings, business_email: e.target.value })}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }}
                    data-testid="business-email-input"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                    Business Phone
                  </label>
                  <input
                    type="tel"
                    value={settings.business_phone || ''}
                    onChange={(e) => setSettings({ ...settings, business_phone: e.target.value })}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }}
                    placeholder="(555) 123-4567"
                    data-testid="business-phone-input"
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settings.email_notifications || false}
                      onChange={(e) => setSettings({ ...settings, email_notifications: e.target.checked })}
                      style={{ width: '18px', height: '18px' }}
                      data-testid="email-notifications-checkbox"
                    />
                    <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Enable Email Notifications</span>
                  </label>
                </div>

                <Button
                  onClick={() => updateSettings(settings)}
                  style={{ backgroundColor: '#10b981', color: 'white', padding: '0.75rem 1.5rem', width: 'fit-content' }}
                  data-testid="save-business-settings-btn"
                >
                  Save Settings
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Operational Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontSize: '1.25rem' }}>Operational Settings</CardTitle>
          </CardHeader>
          <CardContent>
            {settings && (
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                    Max Packages Per Pickup
                  </label>
                  <input
                    type="number"
                    value={settings.max_packages_per_pickup || 10}
                    onChange={(e) => setSettings({ ...settings, max_packages_per_pickup: parseInt(e.target.value) })}
                    style={{ width: '150px', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }}
                    min="1"
                    max="50"
                    data-testid="max-packages-input"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                    Minimum Advance Booking (Hours)
                  </label>
                  <input
                    type="number"
                    value={settings.min_advance_booking_hours || 24}
                    onChange={(e) => setSettings({ ...settings, min_advance_booking_hours: parseInt(e.target.value) })}
                    min="1"
                    max="168"
                    style={{ width: '150px', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }}
                    data-testid="min-advance-hours-input"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Customers must book at least this many hours in advance
                  </p>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settings.same_day_pickup_enabled || false}
                      onChange={(e) => setSettings({ ...settings, same_day_pickup_enabled: e.target.checked })}
                      style={{ width: '18px', height: '18px' }}
                      data-testid="same-day-pickup-checkbox"
                    />
                    <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Enable Same-Day Pickups</span>
                  </label>
                </div>

                {/* Operational Hours */}
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Weekly Availability</h4>
                  {DAYS.map(day => {
                    const daySettings = settings.operational_hours?.[day] || { enabled: true, start: '09:00', end: '17:00' };
                    return (
                      <div key={day} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        marginBottom: '0.75rem',
                        padding: '0.75rem',
                        backgroundColor: daySettings.enabled ? '#f0fdf4' : '#fef2f2',
                        borderRadius: '6px',
                        border: `1px solid ${daySettings.enabled ? '#d1fae5' : '#fee2e2'}`
                      }}>
                        <input
                          type="checkbox"
                          checked={daySettings.enabled}
                          onChange={(e) => setSettings({
                            ...settings,
                            operational_hours: {
                              ...(settings.operational_hours || {}),
                              [day]: { ...daySettings, enabled: e.target.checked }
                            }
                          })}
                          style={{ width: '18px', height: '18px' }}
                          data-testid={`day-${day}-enabled`}
                        />
                        <span style={{
                          fontWeight: '600',
                          width: '100px',
                          textTransform: 'capitalize',
                          color: daySettings.enabled ? '#065f46' : '#991b1b'
                        }}>
                          {day}
                        </span>
                        {daySettings.enabled && (
                          <>
                            <input
                              type="time"
                              value={daySettings.start}
                              onChange={(e) => setSettings({
                                ...settings,
                                operational_hours: {
                                  ...(settings.operational_hours || {}),
                                  [day]: { ...daySettings, start: e.target.value }
                                }
                              })}
                              style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.875rem' }}
                              data-testid={`day-${day}-start`}
                            />
                            <span style={{ color: '#6b7280' }}>to</span>
                            <input
                              type="time"
                              value={daySettings.end}
                              onChange={(e) => setSettings({
                                ...settings,
                                operational_hours: {
                                  ...(settings.operational_hours || {}),
                                  [day]: { ...daySettings, end: e.target.value }
                                }
                              })}
                              style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.875rem' }}
                              data-testid={`day-${day}-end`}
                            />
                          </>
                        )}
                        {!daySettings.enabled && (
                          <span style={{ color: '#991b1b', fontSize: '0.875rem', fontWeight: '500' }}>CLOSED</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                <Button
                  onClick={() => updateSettings(settings)}
                  style={{ backgroundColor: '#10b981', color: 'white', padding: '0.75rem 1.5rem', width: 'fit-content' }}
                  data-testid="save-operational-settings-btn"
                >
                  Save Operational Settings
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Composed sub-settings cards */}
        {settings && <SMSNotificationSettings settings={settings} setSettings={setSettings} updateSettings={updateSettings} />}
        {settings && <ServiceAreaSettings settings={settings} setSettings={setSettings} updateSettings={updateSettings} />}
        {settings && <BlackoutDatesSettings settings={settings} setSettings={setSettings} updateSettings={updateSettings} />}
        {settings && <DynamicPricingSettings settings={settings} setSettings={setSettings} updateSettings={updateSettings} />}
      </div>
    </div>
  );
};

export default AdminSettingsTab;
